import { http, HttpResponse } from 'msw'
import { match, P } from 'ts-pattern'
import { z } from 'zod'
import { DETECTION_LENSES } from '../src/components/beacon/beacon-run.ts'
import {
  type AiScanAlreadyFinishedProblem,
  type AiScanNotReadyProblem,
  type AiScanRunState,
  type BeaconEffort,
  BeaconEffortSchema,
  BeaconPhaseSchema,
  type BeaconProgress,
  type BeaconRun,
  type BeaconRunLinks,
  type BeaconStage,
  type BeaconSummary,
  BeaconSummarySchema,
  type DismissedLead,
  type EntityCoverage,
  type ScanResponse,
} from '../src/utils/api-client/api-schemas.ts'
import {
  type BeaconCandidateRef,
  type BeaconInvestigationRef,
  type BeaconStreamFrame,
  BeaconStreamFrameSchema,
  isTerminalBeaconFrame,
  parseBeaconEvent,
} from '../src/utils/api-client/sse-schemas.ts'
import eventsJson from './fixtures/beacon/events.json' with { type: 'json' }
import summaryJson from './fixtures/beacon/summary.json' with { type: 'json' }
import { type MockFinding, paginateAndFilter } from './mock-findings.ts'
import { FakePaginatedScans, FakeScan } from './models.ts'
import { signedIn } from './session-cookie.ts'

// A stateful model of Pixee Beacon runs shared by dev:mocks, Storybook, vitest and Playwright. A run waits
// queued for capacity, is dispatched, and once analysis-service accepts it replays the Gogs demo run in
// `fixtures/beacon` (built from the approved prototype's data, made contract-conformant) on a compressed
// clock, so a run started in the UI moves through all six lenses, verifies, and finishes with the same
// findings and coverage the prototypes show. The run's scan exists only once the run completes.

/** How long a demo run takes from analysis-service accepting it to its terminal frame. */
export const BEACON_DEMO_DURATION_MS = 90_000

/** How long a run started through the POST waits queued before the platform dispatches it. */
export const BEACON_DEMO_QUEUED_MS = 3_000

/** How long the platform takes to hand a dispatched run to analysis-service. */
export const BEACON_DEMO_DISPATCH_MS = 2_000

/** How long a cancelled run takes to stop after the cancellation is accepted. */
const CANCEL_SETTLE_MS = 2_000

const TOKEN_BUDGET_BY_EFFORT: Record<BeaconEffort, number> = { low: 12_000_000, medium: 24_000_000, high: 36_000_000 }

/** The seeded finished run, so stories and tests can open it by id. */
export const BEACON_COMPLETED_RUN_ID = '0b5e1c0d-beac-4000-8000-000000000002'

/** The scan the seeded finished run's findings became. */
export const BEACON_COMPLETED_SCAN_ID = '0b5e1c0d-beac-4000-8000-000000000001'

type BeaconFixture = { frames: BeaconStreamFrame[]; summary: BeaconSummary; offsetsMs: number[] }

// The prototype's summary also carries an index of every candidate it raised, which the analysis-service
// summary does not. The model reads it for the kept findings' platform rows and the dismissed leads.
const DemoCandidateIndexSchema = z.object({
  findings: z.array(
    z.object({
      cwe_ids: z.array(z.string()),
      file_path: z.string(),
      finding_uuid: z.string(),
      kept: z.boolean(),
      lens: z.string(),
      line: z.number().int(),
      result_type: z.string(),
      title: z.string(),
      tldr: z.string().optional(),
      weakness: z.string(),
    })
  ),
})
type DemoCandidate = z.infer<typeof DemoCandidateIndexSchema>['findings'][number]

let candidates: DemoCandidate[] | undefined

const getDemoCandidates = (): DemoCandidate[] => {
  candidates ??= DemoCandidateIndexSchema.parse(summaryJson).findings
  return candidates
}

type LeadPlacement = Pick<DismissedLead, 'entity_kind' | 'entity_id'>

const REPOSITORY_PLACEMENT: LeadPlacement = { entity_kind: 'repository', entity_id: 'repository' }

/**
 * Where a candidate would have been placed: on an operation in the file it was raised in, else on the
 * part of the surface its investigation focused on — its one entry point, its group, its control — else
 * the repository.
 */
const placementOf = (
  investigation: BeaconInvestigationRef,
  candidate: BeaconCandidateRef,
  coverage: BeaconSummary['coverage']
): LeadPlacement => {
  const { entry_point_ids, control_ids } = investigation.targets
  const operation = coverage.entry_points
    .filter(ep => entry_point_ids.includes(ep.id))
    .flatMap(ep => ep.operations)
    .find(each => each.location.split(':')[0] === candidate.file_path)
  const [entryPointId] = entry_point_ids
  const [controlId] = control_ids
  return (
    match(investigation.focus_kind)
      .when(
        () => !!operation,
        (): LeadPlacement | undefined => (operation ? { entity_kind: 'operation', entity_id: operation.id } : undefined)
      )
      .with('operation_group', (): LeadPlacement | undefined =>
        entryPointId ? { entity_kind: 'group', entity_id: `${entryPointId}::${investigation.label}` } : undefined
      )
      .when(
        () => entry_point_ids.length === 1,
        (): LeadPlacement | undefined =>
          entryPointId ? { entity_kind: 'entry_point', entity_id: entryPointId } : undefined
      )
      .with('control', (): LeadPlacement | undefined =>
        controlId ? { entity_kind: 'control', entity_id: controlId } : undefined
      )
      .otherwise((): LeadPlacement | undefined => ({ entity_kind: 'repository', entity_id: coverage.repository.id })) ??
    REPOSITORY_PLACEMENT
  )
}

/** The first paragraph of a verdict's report: the explanation a reader sees before opening the rest. */
const firstParagraph = (markdown: string) => markdown.split(/\n\s*\n/)[0]?.trim() ?? ''

/**
 * The demo run's dropped candidates as the summary's dismissed leads: refuted ones by the verifier, the
 * rest by triage, each placed where the investigation that raised it was looking.
 */
const demoDismissedLeads = (
  frames: readonly BeaconStreamFrame[],
  coverage: BeaconSummary['coverage']
): DismissedLead[] => {
  const events = frames.map(parseBeaconEvent)
  const refuted = new Set(
    events.flatMap(event =>
      event?.name === 'candidate_verified' && event.detail.verdict === 'refuted' ? [event.detail.finding_uuid] : []
    )
  )
  const placements = new Map(
    events.flatMap(event =>
      event?.name === 'investigation_finished'
        ? event.detail.candidates.map(
            candidate => [candidate.finding_uuid, placementOf(event.detail, candidate, coverage)] as const
          )
        : []
    )
  )
  return getDemoCandidates()
    .filter(candidate => !candidate.kept)
    .map(candidate => ({
      ...(placements.get(candidate.finding_uuid) ?? REPOSITORY_PLACEMENT),
      cwe_ids: candidate.cwe_ids,
      // Like analysis-service's report, the details open with the explanation and go on from there.
      details: [
        candidate.tldr ?? '',
        `Raised at \`${candidate.file_path}:${candidate.line}\` as ${candidate.weakness}${candidate.cwe_ids.length ? ` (${candidate.cwe_ids.join(', ')})` : ''}.`,
      ]
        .filter(Boolean)
        .join('\n\n'),
      dismissed_by: refuted.has(candidate.finding_uuid) ? 'verifier' : 'triage',
      explanation: firstParagraph(candidate.tldr ?? ''),
      file_path: candidate.file_path,
      finding_uuid: candidate.finding_uuid,
      lens: candidate.lens,
      line_end: candidate.line,
      line_start: candidate.line,
      title: candidate.title,
      weakness: candidate.weakness,
    }))
}

let fixture: BeaconFixture | undefined

/**
 * Parsed on first use rather than at import: every vitest file loads the default handlers, and only the
 * Beacon tests need ~600 KB of fixture validated.
 */
export const getBeaconFixture = (): BeaconFixture => {
  if (!fixture) {
    const frames = BeaconStreamFrameSchema.array().parse(eventsJson)
    const realMs = frames.map(frame => Date.parse(frame.ts))
    // Idle gaps (the model thinking) are capped at five seconds before the run is scaled to its duration,
    // so one three-minute pause does not swallow the demo while each stage keeps its share of the run.
    const compressed = realMs.reduce<number[]>((acc, ms, index) => {
      const gap = index === 0 ? 0 : ms - (realMs[index - 1] ?? ms)
      return [...acc, (acc.at(-1) ?? 0) + (gap > 5_000 ? 5_000 : gap)]
    }, [])
    const total = compressed.at(-1) || 1
    const summary = BeaconSummarySchema.parse(summaryJson)
    fixture = {
      frames,
      summary: { ...summary, dismissed: demoDismissedLeads(frames, summary.coverage) },
      offsetsMs: compressed.map(offset => offset / total),
    }
  }
  return fixture
}

const withoutFindings = <T extends EntityCoverage>(entity: T): T => ({ ...entity, findings: [] })

/**
 * A summary as if every suspected weakness had been ruled out and every check had run: a complete verdict
 * with no verified findings, whose dismissed leads are the whole result.
 */
export const zeroFindingsSummary = (summary: BeaconSummary): BeaconSummary => {
  const { coverage } = summary
  return {
    ...summary,
    counts: { ...summary.counts, true_positive: 0, uncertain: 0, findings_kept: 0 },
    finding_placements: [],
    coverage: {
      ...coverage,
      examined_percent: 100,
      entry_points: coverage.entry_points.map(ep => ({
        ...withoutFindings(ep),
        status: 'examined' as const,
        operations: ep.operations.map(withoutFindings),
        groups: ep.groups.map(group => ({ ...group, findings: [] })),
      })),
      assets: coverage.assets.map(withoutFindings),
      controls: coverage.controls.map(withoutFindings),
      repository: withoutFindings(coverage.repository),
      proof: {
        ...coverage.proof,
        alarms: [],
        lenses: coverage.proof.lenses.map(lens =>
          lens.lens === 'ai_scan_sink_backward'
            ? { ...lens, planned: 4, run: 4, spent: 180_000, allotted: 180_000 }
            : lens
        ),
        verdict: {
          level: 'on',
          headline: 'Beacon examined 100% of gogs’s attack surface',
          sentence:
            '9 of 9 entry points, 8 of 8 sensitive assets and 11 of 11 checkable security controls; 145 files read.',
        },
      },
    },
  }
}

type DemoRun = {
  runId: string
  /** The scan the run's findings become; the platform reports it only once the run completed. */
  scanId: string
  repositoryId: string
  effort: BeaconEffort
  queuedAtMs: number
  /** How long the run waits queued before the platform dispatches it. */
  queuedForMs: number
  /** How long the platform takes to hand the run to analysis-service. */
  dispatchMs: number
  /** Start to terminal frame once analysis-service accepted the run; 0 for a run seeded already finished. */
  durationMs: number
  /** Whether the run waits for analysis-service capacity, so it reports when it is next tried. */
  isWaitingForCapacity?: boolean
  /** Fails the run with this reason once the given share of its duration has passed. */
  failure?: { reason: string; atFraction: number }
  /** When a cancellation was accepted, and how many frames the run had emitted by then. */
  cancelledAt?: { atMs: number; seq: number }
  /** Completes with every lead dismissed and no verified finding. */
  hasNoFindings?: boolean
}

/** Start a run with these to make it wait for capacity, fail, or take a given time. */
export type DemoRunOptions = Partial<
  Pick<
    DemoRun,
    'effort' | 'queuedForMs' | 'dispatchMs' | 'durationMs' | 'isWaitingForCapacity' | 'failure' | 'hasNoFindings'
  >
>

const runs = new Map<string, DemoRun>()
let seededRepositoryIds: readonly string[] = []

// Under dev:mocks the handlers run in the page, so a reload would forget every started run. Keeping the
// runs in localStorage lets a reload land on the same run, which is the behaviour being demonstrated.
const STORAGE_KEY = 'pixee-mocks/beacon-runs'

const browserStorage = (): Storage | undefined => {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage
  } catch {
    return undefined
  }
}

const persistRuns = () =>
  browserStorage()?.setItem(STORAGE_KEY, JSON.stringify([...runs.values()].filter(run => run.durationMs > 0)))

const restoreRuns = () => {
  const stored = browserStorage()?.getItem(STORAGE_KEY)
  const parsed: unknown = stored ? JSON.parse(stored) : []
  if (Array.isArray(parsed)) {
    for (const run of parsed as DemoRun[]) {
      if (typeof run.runId === 'string') {
        runs.set(run.runId, run)
      }
    }
  }
}

const addRun = (run: DemoRun) => {
  runs.set(run.runId, run)
  persistRuns()
}

const finishedRun = (repositoryId: string): DemoRun => ({
  runId: BEACON_COMPLETED_RUN_ID,
  scanId: BEACON_COMPLETED_SCAN_ID,
  repositoryId,
  effort: 'medium',
  queuedAtMs: Date.parse('2026-10-09T16:04:30Z'),
  queuedForMs: 0,
  dispatchMs: 0,
  durationMs: 0,
})

const seedFinishedRun = () => {
  const [first] = seededRepositoryIds
  if (first) {
    runs.set(BEACON_COMPLETED_RUN_ID, finishedRun(first))
  }
}

/** The first repository gets a finished run, so the Repositories column shows a mix of states. */
export const seedBeaconRuns = (repositoryIds: readonly string[]) => {
  seededRepositoryIds = repositoryIds
  seedFinishedRun()
  restoreRuns()
}

/** The seeded completed run as the platform reports it, for stories and tests that render it directly. */
export const completedBeaconRun = (): BeaconRun =>
  beaconRunSnapshot(runs.get(BEACON_COMPLETED_RUN_ID) ?? finishedRun(seededRepositoryIds[0] ?? 'repository'))

/** Clears started runs and re-seeds the finished one. Both harnesses call this between tests. */
export const resetBeaconState = () => {
  runs.clear()
  browserStorage()?.removeItem(STORAGE_KEY)
  seedFinishedRun()
}

const dispatchedAtMs = (run: DemoRun) => run.queuedAtMs + run.queuedForMs
const acceptedAtMs = (run: DemoRun) => dispatchedAtMs(run) + run.dispatchMs
const isCancelledBeforeAccepted = (run: DemoRun) => !!run.cancelledAt && run.cancelledAt.atMs < acceptedAtMs(run)
const isCancelledAt = (run: DemoRun, nowMs: number) => !!run.cancelledAt && run.cancelledAt.atMs <= nowMs

/** The frames analysis-service has emitted for a run by `nowMs`: the fixture's on the run's clock, plus a failure's or cancellation's. */
const framesAt = (run: DemoRun, nowMs: number): BeaconStreamFrame[] => {
  if (isCancelledBeforeAccepted(run) || nowMs < acceptedAtMs(run)) {
    return []
  }
  const { frames, offsetsMs } = getBeaconFixture()
  const startMs = acceptedAtMs(run)
  const toRunFrame = (frame: BeaconStreamFrame, index: number): BeaconStreamFrame => {
    // A seeded finished run keeps the recorded timestamps, so its Run view shows the real 27 minutes.
    const ts =
      run.durationMs === 0 ? frame.ts : new Date(startMs + (offsetsMs[index] ?? 0) * run.durationMs).toISOString()
    if (frame.name !== 'scan_configured') {
      return { ...frame, ts }
    }
    const tokenBudget = TOKEN_BUDGET_BY_EFFORT[run.effort]
    return {
      ...frame,
      ts,
      detail: { ...frame.detail, effort: run.effort, token_budget: tokenBudget },
      message: `Starting a ${run.effort}-effort Beacon scan with a ${tokenBudget / 1_000_000}M token budget.`,
    }
  }
  const clock = run.durationMs === 0 ? Infinity : nowMs - startMs
  const failAfterMs = run.failure ? run.failure.atFraction * run.durationMs : Infinity
  const emitted = frames
    .map(toRunFrame)
    .filter((_, index) => {
      const offsetMs = (offsetsMs[index] ?? 0) * run.durationMs
      return offsetMs <= clock && offsetMs < failAfterMs
    })
    .filter(frame => !run.failure || !isTerminalBeaconFrame(frame))
  if (run.failure && clock >= failAfterMs) {
    return [
      ...emitted,
      {
        seq: (emitted.at(-1)?.seq ?? 0) + 1,
        ts: new Date(startMs + failAfterMs).toISOString(),
        status: 'failed',
        name: 'failed',
        detail: {},
        message: run.failure.reason,
      },
    ]
  }
  if (!run.cancelledAt) {
    return emitted
  }
  const ts = new Date(run.cancelledAt.atMs).toISOString()
  const kept = emitted.slice(0, run.cancelledAt.seq)
  const status = kept.at(-1)?.status ?? 'queued'
  const requested = {
    seq: run.cancelledAt.seq + 1,
    ts,
    status,
    name: 'cancel_requested',
    detail: {},
    message: 'Cancellation requested.',
  }
  // analysis-service reports the scan stopped a moment after the platform cancelled the run.
  const stoppedAtMs = run.cancelledAt.atMs + CANCEL_SETTLE_MS
  if (nowMs < stoppedAtMs) {
    return [...kept, requested]
  }
  return [
    ...kept,
    requested,
    {
      seq: run.cancelledAt.seq + 2,
      ts: new Date(stoppedAtMs).toISOString(),
      status: 'cancelled',
      name: 'cancelled',
      detail: {},
      message: 'Scan cancelled; nothing further runs.',
    },
  ]
}

type RunProgress = {
  stage?: BeaconStage
  investigationsTotal?: number
  lensesDone: number
  investigationsDone: number
  /** Candidates raised so far; deduplicated once verification is about to start. */
  candidates: number
  verifyTotal?: number
  /** Candidates through verification and triage. */
  verified: number
  kept: number
  examinedPercent?: number
  terminal?: { name: 'completed' | 'failed' | 'cancelled'; ts: string; message: string }
}

const INITIAL_PROGRESS: RunProgress = { lensesDone: 0, investigationsDone: 0, candidates: 0, verified: 0, kept: 0 }

/**
 * The progress the platform reports for a run, folded from its frames as analysis-service's
 * `reduce_progress` (workflow/ai_scan/progress.py) folds them, so the mock's snapshot can disagree
 * with the UI's own fold exactly where the real one would.
 */
const progressOf = (frames: readonly BeaconStreamFrame[]): RunProgress =>
  frames
    .map(parseBeaconEvent)
    .filter(event => event !== undefined)
    .reduce<RunProgress>(
      (acc, event) =>
        match(event)
          .with({ name: 'stage_started', detail: { stage: 'verify' } }, () => ({
            ...acc,
            stage: 'verify' as const,
            verifyTotal: acc.candidates,
          }))
          .with({ name: 'stage_started' }, ({ detail }) => ({ ...acc, stage: detail.stage }))
          .with({ name: 'plan_built' }, ({ detail }) => ({ ...acc, investigationsTotal: detail.investigations.length }))
          .with({ name: 'investigation_finished', detail: { outcome: 'skipped' } }, () => acc)
          .with({ name: 'investigation_finished' }, ({ detail }) => ({
            ...acc,
            investigationsDone: acc.investigationsDone + 1,
            candidates: acc.candidates + detail.candidates.length,
            kept: acc.kept + detail.candidates.length,
          }))
          .with({ name: 'lens_finished' }, ({ detail }) =>
            (DETECTION_LENSES as readonly string[]).includes(detail.lens)
              ? { ...acc, lensesDone: Math.min(acc.lensesDone + 1, DETECTION_LENSES.length) }
              : acc
          )
          .with({ name: 'candidates_deduped' }, ({ detail }) => ({
            ...acc,
            candidates: detail.deduped,
            kept: detail.deduped,
          }))
          .with({ name: 'candidate_triaged' }, ({ detail }) => ({
            ...acc,
            verified: acc.verified + 1,
            kept: acc.kept - (detail.kept ? 0 : 1),
          }))
          .with({ name: 'summary_ready' }, ({ detail }) => ({ ...acc, examinedPercent: detail.examined_percent }))
          .with({ name: P.union('completed', 'failed', 'cancelled') }, ({ name, ts, message }) => ({
            ...acc,
            terminal: { name, ts, message },
          }))
          .otherwise(() => acc),
      INITIAL_PROGRESS
    )

type RunIdentity = Pick<DemoRun, 'runId' | 'scanId' | 'repositoryId' | 'effort' | 'queuedAtMs' | 'hasNoFindings'>

const DEMO_SHA = '3fce0c0d9b1e5a7c2f4d6e8a0b1c3d5e7f9a1b2c'

/** The run's links: its scan and coverage only once it completed, as the platform serves them. */
const runLinks = (run: RunIdentity, isCompleted = false): BeaconRunLinks => ({
  self: { href: `/api/v1/ai-scans/${run.runId}` },
  repository: { href: `/api/v1/repositories/${run.repositoryId}` },
  events: { href: `/api/v1/ai-scans/${run.runId}/events` },
  scan: isCompleted ? { href: `/api/v1/scans/${run.scanId}` } : undefined,
  coverage: isCompleted ? { href: `/api/v1/ai-scans/${run.runId}/coverage` } : undefined,
})

const runBase = (run: RunIdentity) => ({
  id: run.runId,
  branch: 'main',
  effort: run.effort,
  requested_by: 'demo@pixee.ai',
  queued_at: new Date(run.queuedAtMs).toISOString(),
  _links: runLinks(run),
})

/** What analysis-service last reported, absent until it reported anything, as the platform omits it. */
const progressResponse = (
  run: RunIdentity,
  frames: readonly BeaconStreamFrame[],
  progress: RunProgress
): BeaconProgress | undefined =>
  frames.length === 0
    ? undefined
    : {
        phase: progress.terminal?.name ?? BeaconPhaseSchema.safeParse(frames.at(-1)?.status).data ?? 'queued',
        token_budget: TOKEN_BUDGET_BY_EFFORT[run.effort],
        stage: progress.stage,
        lenses: { done: progress.lensesDone, total: DETECTION_LENSES.length },
        investigations: { done: progress.investigationsDone, total: progress.investigationsTotal },
        verification: { done: progress.verified, total: progress.verifyTotal },
        findings_kept: progress.kept,
        examined_percent: progress.examinedPercent,
      }

/**
 * The snapshot the platform keeps for a run analysis-service accepted and that has emitted exactly
 * `frames`: its progress reduced from them, its state from the terminal frame, if any.
 */
export const snapshotFromFrames = (run: RunIdentity, frames: readonly BeaconStreamFrame[]): BeaconRun => {
  const progress = progressOf(frames)
  // A run seeded with no findings keeps none, whatever its frames raised.
  const kept = run.hasNoFindings ? 0 : progress.kept
  const accepted = { ...runBase(run), sha: DEMO_SHA, progress: progressResponse(run, frames, { ...progress, kept }) }
  return match(progress.terminal)
    .returnType<BeaconRun>()
    .with(undefined, () => ({
      ...accepted,
      state: 'in-progress',
      state_changed_at: frames[0]?.ts ?? new Date(run.queuedAtMs).toISOString(),
    }))
    .with({ name: 'completed' }, ({ ts }) => ({
      ...accepted,
      state: 'completed',
      state_changed_at: ts,
      findings_count: kept,
      _links: runLinks(run, true),
    }))
    .with({ name: 'failed' }, ({ ts, message }) => ({
      ...accepted,
      state: 'failed',
      state_changed_at: ts,
      failure_reason: message,
    }))
    .with({ name: 'cancelled' }, ({ ts }) => ({ ...accepted, state: 'cancelled', state_changed_at: ts }))
    .exhaustive()
}

type Lifecycle = 'cancelled' | 'queued' | 'dispatching' | 'accepted'

const lifecycleAt = (run: DemoRun, nowMs: number): Lifecycle =>
  match(nowMs)
    .when(
      now => isCancelledAt(run, now),
      (): Lifecycle => 'cancelled'
    )
    .when(
      now => now < dispatchedAtMs(run),
      (): Lifecycle => 'queued'
    )
    .when(
      now => now < acceptedAtMs(run),
      (): Lifecycle => 'dispatching'
    )
    .otherwise((): Lifecycle => 'accepted')

/** The run as the platform reports it at `nowMs`: queued, dispatching, or reduced from its frames. */
export const beaconRunSnapshot = (run: DemoRun, nowMs = Date.now()): BeaconRun =>
  match(lifecycleAt(run, nowMs))
    .with('cancelled', () => {
      // What analysis-service had reported when the platform cancelled the run.
      const reported = framesAt(run, nowMs).slice(0, run.cancelledAt?.seq ?? 0)
      return {
        ...runBase(run),
        state: 'cancelled' as const,
        state_changed_at: new Date(run.cancelledAt?.atMs ?? nowMs).toISOString(),
        sha: isCancelledBeforeAccepted(run) ? undefined : DEMO_SHA,
        progress: progressResponse(run, reported, progressOf(reported)),
      }
    })
    .with('queued', () => ({
      ...runBase(run),
      state: 'queued' as const,
      state_changed_at: new Date(run.queuedAtMs).toISOString(),
      retry_after: run.isWaitingForCapacity ? new Date(dispatchedAtMs(run)).toISOString() : undefined,
    }))
    .with('dispatching', () => ({
      ...runBase(run),
      state: 'dispatching' as const,
      state_changed_at: new Date(dispatchedAtMs(run)).toISOString(),
    }))
    .with('accepted', () => snapshotFromFrames(run, framesAt(run, nowMs)))
    .exhaustive()

const completedRunOfScan = (scanId: string) =>
  [...runs.values()].find(run => run.scanId === scanId && beaconRunSnapshot(run).state === 'completed')

/** A completed run's scan: an ordinary platform scan whose findings arrived already triaged. */
const scanOf = (run: DemoRun, origin: string): ScanResponse => {
  const snapshot = beaconRunSnapshot(run)
  return FakeScan({
    id: run.scanId,
    detector: 'pixee_ai',
    branch: 'main',
    sha: DEMO_SHA,
    imported_at: snapshot.state_changed_at,
    scanned_at: snapshot.state_changed_at,
    html_url: `${origin}/beacon/runs/${run.runId}`,
    _links: {
      repository: { href: `/api/v1/repositories/${run.repositoryId}`, title: 'gogs/gogs' },
      findings: { href: `/api/v1/scans/${run.scanId}/findings` },
    },
  })
}

const latestRunOf = (repositoryId: string) =>
  [...runs.values()].filter(run => run.repositoryId === repositoryId).sort((a, b) => b.queuedAtMs - a.queuedAtMs)[0]

const UNFINISHED_STATES: ReadonlySet<AiScanRunState> = new Set(['queued', 'dispatching', 'in-progress'])

const isUnfinished = (run: DemoRun, nowMs = Date.now()) => UNFINISHED_STATES.has(beaconRunSnapshot(run, nowMs).state)

const SEVERITY_BY_RESULT: Record<string, MockFinding['severity']> = {
  TRUE_POSITIVE: { label: 'high', rank: 8 },
  UNCERTAIN: { label: 'medium', rank: 5 },
}

/** The run's kept findings as platform findings: already triaged, like any other scanner's scan. */
export const beaconMockFindings = (): MockFinding[] =>
  getDemoCandidates()
    .filter(candidate => candidate.kept)
    .map(candidate => ({
      id: candidate.finding_uuid,
      title: candidate.title,
      rule: candidate.cwe_ids[0] ?? candidate.weakness,
      severity: SEVERITY_BY_RESULT[candidate.result_type] ?? { label: 'low', rank: 3 },
      suggestedSeverity: SEVERITY_BY_RESULT[candidate.result_type] ?? null,
      triage: {
        status: 'completed',
        suggestedStatus: candidate.result_type === 'TRUE_POSITIVE' ? 'true_positive' : 'suspicious',
      },
      // A finding is only analyzed once fix has settled after a successful triage, so these carry a
      // fix without recommendations rather than none — with no fix they would read as still in
      // progress and drop out of the default Analyzed findings view.
      fix: { status: 'no_recommendations' },
      sca: null,
      severityUpdate: 'no_update',
    }))

const problem = (body: { status: number; type: string; title: string; detail: string } & Record<string, unknown>) =>
  HttpResponse.json(body, { status: body.status, headers: { 'Content-Type': 'application/problem+json' } })

const notReady = (run: DemoRun, detail: string) =>
  problem({
    type: 'pixee:problems/api/ai-scans/not-ready',
    title: 'The run is not ready',
    status: 409,
    detail,
    state: beaconRunSnapshot(run).state,
  } satisfies AiScanNotReadyProblem & { type: string })

const notFound = () => new HttpResponse(null, { status: 404 })

// Each event carries its seq as its SSE id, as the platform sends them.
const sseFrame = (frame: BeaconStreamFrame) =>
  `id: ${frame.seq}\nevent: ${frame.name}\ndata: ${JSON.stringify(frame)}\n\n`

const SSE_HEARTBEAT = ': heartbeat\n\n'

const SSE_HEADERS = { 'Content-Type': 'text/event-stream', 'Cache-Control': 'no-cache' }

export type BeaconHandlersOptions = DemoRunOptions & {
  /**
   * Whether the events endpoint keeps the connection open and tails new frames. Playwright's route
   * interception only fulfils complete bodies, so e2e runs answer with the frames so far and close,
   * and the client reconnects — the same path a proxy dropping the connection exercises.
   */
  isStreaming?: boolean
  /** How often a streaming connection checks for new frames. */
  tickMs?: number
  /** How long a streaming connection goes without a frame before it sends a heartbeat comment. */
  heartbeatMs?: number
}

const createRun = ({
  repositoryId,
  effort = 'medium',
  queuedAtMs = Date.now(),
  queuedForMs = 0,
  dispatchMs = 0,
  durationMs = BEACON_DEMO_DURATION_MS,
  isWaitingForCapacity,
  failure,
  hasNoFindings,
}: DemoRunOptions & { repositoryId: string; queuedAtMs?: number }): DemoRun => {
  const run: DemoRun = {
    runId: crypto.randomUUID(),
    scanId: crypto.randomUUID(),
    repositoryId,
    effort,
    queuedAtMs,
    queuedForMs,
    dispatchMs,
    durationMs,
    isWaitingForCapacity,
    failure,
    hasNoFindings,
  }
  addRun(run)
  return run
}

export const beaconHandlers = ({
  isStreaming = true,
  tickMs = 250,
  heartbeatMs = 5_000,
  queuedForMs = BEACON_DEMO_QUEUED_MS,
  dispatchMs = BEACON_DEMO_DISPATCH_MS,
  ...runOptions
}: BeaconHandlersOptions = {}) => ({
  postBeaconScan: http.post(
    '/api/v1/repositories/:repositoryId/ai-scans',
    signedIn(async ({ params, request }) => {
      const repositoryId = String(params.repositoryId)
      const latest = latestRunOf(repositoryId)
      if (latest && isUnfinished(latest)) {
        return HttpResponse.json(beaconRunSnapshot(latest), { status: 200 })
      }
      const body: unknown = await request.json().catch(() => ({}))
      const effort =
        BeaconEffortSchema.safeParse(typeof body === 'object' && body && 'effort' in body ? body.effort : undefined)
          .data ?? 'medium'
      const run = createRun({ ...runOptions, repositoryId, effort, queuedForMs, dispatchMs })
      return HttpResponse.json(beaconRunSnapshot(run), { status: 202 })
    })
  ),

  getRepositoryLatestBeaconRun: http.get(
    '/api/v1/repositories/:repositoryId/ai-scans/latest',
    signedIn(({ params }) => {
      const run = latestRunOf(String(params.repositoryId))
      return run ? HttpResponse.json(beaconRunSnapshot(run)) : notFound()
    })
  ),

  // Before the run-by-id handler, which would otherwise read `latest` as a run id.
  getLatestBeaconRuns: http.get(
    '/api/v1/ai-scans/latest',
    signedIn(({ request }) => {
      const repositoryIds = new URL(request.url).searchParams.getAll('repository-id')
      const items = repositoryIds.flatMap(id => {
        const run = latestRunOf(id)
        return run ? [beaconRunSnapshot(run)] : []
      })
      return HttpResponse.json({ items })
    })
  ),

  getBeaconRun: http.get(
    '/api/v1/ai-scans/:runId',
    signedIn(({ params }) => {
      const run = runs.get(String(params.runId))
      return run ? HttpResponse.json(beaconRunSnapshot(run)) : notFound()
    })
  ),

  getBeaconRunCoverage: http.get(
    '/api/v1/ai-scans/:runId/coverage',
    signedIn(({ params }) => {
      const run = runs.get(String(params.runId))
      if (!run) {
        return notFound()
      }
      if (beaconRunSnapshot(run).state !== 'completed') {
        return notReady(run, 'The run has not completed, so it has no coverage summary.')
      }
      const { summary } = getBeaconFixture()
      return HttpResponse.json({
        ...(run.hasNoFindings ? zeroFindingsSummary(summary) : summary),
        effort: run.effort,
        token_budget: TOKEN_BUDGET_BY_EFFORT[run.effort],
      })
    })
  ),

  getBeaconRunEvents: http.get(
    '/api/v1/ai-scans/:runId/events',
    signedIn(({ params, request }) => {
      const run = runs.get(String(params.runId))
      if (!run) {
        return notFound()
      }
      // Like the platform, the stream ends once the run has finished, whether or not a terminal frame
      // reached it: a run cancelled while queued has no frames at all.
      const hasEnded = (frames: readonly BeaconStreamFrame[]) =>
        frames.some(isTerminalBeaconFrame) || !isUnfinished(run)
      // A client that reconnects with the last id it received resumes after it; one without replays all.
      let lastSeq = Number(request.headers.get('Last-Event-ID')) || 0
      const framesAfterLastSeq = () => framesAt(run, Date.now()).filter(frame => frame.seq > lastSeq)
      const replay = framesAfterLastSeq()
      lastSeq = replay.at(-1)?.seq ?? lastSeq
      if (!isStreaming || hasEnded(replay)) {
        return new HttpResponse(replay.map(sseFrame).join(''), { headers: SSE_HEADERS })
      }
      const encoder = new TextEncoder()
      let idleMs = 0
      let timer: ReturnType<typeof setInterval> | undefined
      const stream = new ReadableStream<Uint8Array>({
        start: controller => {
          controller.enqueue(encoder.encode(replay.map(sseFrame).join('')))
          const stop = () => {
            clearInterval(timer)
            try {
              controller.close()
            } catch {
              // Already closed by the client going away.
            }
          }
          request.signal.addEventListener('abort', stop)
          timer = setInterval(() => {
            const fresh = framesAfterLastSeq()
            lastSeq = fresh.at(-1)?.seq ?? lastSeq
            idleMs = fresh.length > 0 ? 0 : idleMs + tickMs
            if (fresh.length > 0) {
              controller.enqueue(encoder.encode(fresh.map(sseFrame).join('')))
            } else if (idleMs >= heartbeatMs) {
              idleMs = 0
              controller.enqueue(encoder.encode(SSE_HEARTBEAT))
            }
            if (hasEnded(fresh)) {
              stop()
            }
          }, tickMs)
        },
        cancel: () => clearInterval(timer),
      })
      return new HttpResponse(stream, { headers: SSE_HEADERS })
    })
  ),

  postBeaconCancellation: http.post(
    '/api/v1/ai-scans/:runId/cancellation',
    signedIn(({ params }) => {
      const run = runs.get(String(params.runId))
      if (!run) {
        return notFound()
      }
      const nowMs = Date.now()
      const { state } = beaconRunSnapshot(run, nowMs)
      if (state === 'completed' || state === 'failed') {
        return problem({
          type: 'pixee:problems/api/ai-scans/already-finished',
          title: 'Pixee AI scan already finished',
          status: 409,
          detail: 'The scan already completed or failed, so there is nothing to cancel.',
        } satisfies AiScanAlreadyFinishedProblem & { type: string })
      }
      if (state !== 'cancelled') {
        run.cancelledAt = { seq: framesAt(run, nowMs).length, atMs: nowMs }
        persistRuns()
      }
      return HttpResponse.json(beaconRunSnapshot(run, nowMs))
    })
  ),

  // The platform's own scan endpoints answer for completed runs' scans and fall through for every other
  // scan, so the generic scan mocks stay in charge of the rest of the app.
  getBeaconScan: http.get(
    '/api/v1/scans/:scanId',
    signedIn(({ params, request }) => {
      const run = completedRunOfScan(String(params.scanId))
      return run ? HttpResponse.json(scanOf(run, new URL(request.url).origin)) : undefined
    })
  ),

  getBeaconScans: http.get(
    '/api/v1/scans',
    signedIn(({ request }) => {
      const url = new URL(request.url)
      const tools = url.searchParams.getAll('tools')
      if (tools.length !== 1 || tools[0] !== 'pixee_ai') {
        return undefined
      }
      const repositoryIds = url.searchParams.getAll('repository-ids')
      const pageNumber = Number(url.searchParams.get('page-number')) || 0
      const pageSize = Number(url.searchParams.get('page-size')) || 10
      const analysisState = url.searchParams.get('analysis-state')
      const scans = [...runs.values()]
        .filter(run => repositoryIds.length === 0 || repositoryIds.includes(run.repositoryId))
        .filter(run => beaconRunSnapshot(run).state === 'completed')
        // A completed run's findings arrive triaged, so its scan lists as analyzed and never as anything else.
        .filter(() => !analysisState || analysisState === 'completed')
        .sort((a, b) => b.queuedAtMs - a.queuedAtMs)
        .map(run => scanOf(run, url.origin))
      return HttpResponse.json(
        FakePaginatedScans({
          total: scans.length,
          page: { number: pageNumber, size: pageSize },
          _embedded: { items: scans.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize) },
          distinct_branches: scans.length > 0 ? ['main'] : [],
        })
      )
    })
  ),

  getBeaconScanFindings: http.get(
    '/api/v1/scans/:scanId/findings',
    signedIn(({ params, request }) => {
      const run = completedRunOfScan(String(params.scanId))
      return run
        ? HttpResponse.json(
            paginateAndFilter(run.hasNoFindings ? [] : beaconMockFindings(), new URL(request.url).searchParams)
          )
        : undefined
    })
  ),
})

/**
 * Starts a run directly, for tests that need one without going through the POST. Unless asked otherwise
 * it skips the queue, so analysis-service has accepted it at `queuedAtMs`.
 */
export const startBeaconDemoRun = (options: DemoRunOptions & { repositoryId: string; queuedAtMs?: number }) =>
  beaconRunSnapshot(createRun(options))
